/**
 * Knowledge at the moment it's needed (ONTOLOGICAL_CENTER dimension 4): every check says what you need, how to do it,
 * and why, in plain words. All wording comes from docs/source (the protocols) and the Food Code sections they cite.
 */
import type { SanitizerSetup } from "./sanitizer.ts";
import { HOWTO_UT } from "./states/ut/howto.ts";
import { perStateTable } from "./states/access.ts";
import type { StateWords, UsState } from "./states/types.ts";
import { cite, wordsFor } from "./states/words.ts";
import { STRIP_FOR, needsStripTest, type SanitizerChemical } from "./sanitizer.ts";

export interface HowTo {
  need: string[]; steps: string[]; why: string; citations: string[];
  /** Supplies this how-to uses, so the app can say where they're kept and how to get more (actions.ts supply keys). */
  needSupplies?: { key: string; product: string }[];
  /** A "What you need" line with nothing to buy, and why (the catalog names no purpose for it): test/guide-needs.test.ts. */
  noBuy?: Record<string, string>;
}

const CLOTHS: HowTo = {
  need: ["Your cloth bucket with the cloths in it", "Test strips that match your sanitizer", "Clean cloths (microfiber or quat-safe)"],
  steps: [
    "Test the bucket with the cloth in it. Cloths weaken sanitizer.",
    "Dump it if weak or cloudy. Mix fresh and test again.",
    "Rinse milk or food off a cloth before it goes back.",
    "Keep the bucket off the floor, away from food, cups and dishes.",
    "Use cloths only for spills and wiping counters.",
    "Wash, rinse, sanitize and air-dry the surfaces food touches instead.",
    "Keep restroom and floor cloths in their own labeled bucket.",
    "Never bring them into the kitchen.",
    "Make sure wet cloths go to the wash at day's end.",
    "Wash them apart from home laundry, in hot water with detergent.",
    "Dry them all the way.",
  ],
  why: "A weak bucket turns a cloth into a germ spreader. A strong one keeps counters safe between cleanings.",
  citations: ["Food Code 3-304.14", "Food Code 4-802.11", "Source protocols: wiping cloths and laundry"],
};

/** SANITIZER_RANGE (sanitizer.ts), re-said in plain words: same numbers, same wait times. */
const PLAIN_RANGE: Record<SanitizerChemical, string[]> = {
  chlorine: ["Look for the number on your bleach label, never under 50.", "Keep surfaces wet for the label's time. Clorox bleach says 2 minutes."],
  quat: ["Look for 200 to 400 on the chart, or the label's number.", "Keep surfaces wet for the label's time. Steramine says 1 minute."],
  iodine: ["Look for 12.5 to 25 on the chart."],
};

export function sanitizerHowTo(s: SanitizerSetup | undefined, recheck: boolean): HowTo {
  if (!needsStripTest(s)) return {
    need: ["Your ready-to-use sanitizer spray", "Paper towels (to wipe off food first)"],
    steps: [
      "Check that it is the right food-contact, no-rinse spray.",
      "Check that it is not expired.",
      "Wipe off food and crumbs first. Sanitizer doesn't work on dirt.",
      "Spray until the surface is wet.",
      "Leave it wet for the time on the label.",
      "Let it air-dry. Don't wipe it off.",
    ],
    why: "Ready-to-use sprays come at the right strength, so no strip is needed. They only work if the surface stays wet long enough.",
    citations: ["Source protocols: ready-to-use no-rinse sprays, use as labeled", "Food Code 4-501.114"],
  };
  const chem = s?.chemical;
  const strip = chem ? STRIP_FOR[chem] : "test strips that match your sanitizer (quat strips for quat, chlorine strips for bleach)";
  // SANITIZER_RANGE in plain words (the strip chart shows the numbers, so the step names them without "ppm").
  const range = chem ? PLAIN_RANGE[chem] : ["Look for the strength on your sanitizer's label."];
  const mix: HowTo = {
    need: [
      `${strip.charAt(0).toUpperCase()}${strip.slice(1)}. The strip must match the sanitizer, or the test means nothing.`,
      "A clean cup to dip into, or dip straight into the sink or bucket",
      "A clock or phone timer",
      s?.product ? `${s.product} and water, mixed as the label says` : "Your sanitizer and water, mixed as the label says",
    ],
    steps: [
      s?.methods.includes("dispenser") && !s.methods.includes("mix") ? "Fill the sink or bucket from the dispenser." : "Mix the sanitizer with water exactly as the label says.",
      chem === "quat" ? "Dip one Hydrion QT-40 quat strip. Time it by the vial." : chem === "chlorine" ? "Dip one Hydrion chlorine strip. Time it by the vial." : chem === "iodine" ? "Dip one Hydrion iodine strip. Time it by the vial." : "Dip one strip that matches your sanitizer. Time it by the vial.",
      "Hold it next to the color chart on the vial right away.",
      "Find the closest color.",
      ...range,
      "Add a little sanitizer if it is too weak.",
      "Add water if it is too strong.",
      "Test again with a new strip.",
      "Type the number you read.",
      "Close the vial so the strips stay dry.",
    ],
    why: "Too weak and germs survive. Too strong and chemicals can end up on food.",
    citations: ["Food Code 4-302.14", "Food Code 4-501.114", "Source protocols: testing strength"],
    needSupplies: [{ key: "test-strips", product: chem ? STRIP_PRODUCT[chem] : "Hydrion QT-40 quat test strips" }],
  };
  if (!recheck) return mix;
  return { need: [...new Set([...mix.need.slice(0, 3), ...CLOTHS.need])], steps: [...mix.steps.slice(1), ...CLOTHS.steps], why: `${mix.why} ${CLOTHS.why}`, citations: [...mix.citations, ...CLOTHS.citations], needSupplies: mix.needSupplies };
}

/** The exact strip for each sanitizer (source protocols' shopping list; research/sanitizer-selection.md S26). */
const STRIP_PRODUCT: Record<SanitizerChemical, string> = {
  quat: "Hydrion QT-40 quat test strips 0-400 ppm", chlorine: "Hydrion chlorine test strips 0-200 ppm", iodine: "Hydrion iodine test strips",
};


const WORDS: Record<UsState, StateWords<typeof HOWTO_UT>> = perStateTable("HOWTO", HOWTO_UT);
const words = (state?: UsState) => wordsFor(WORDS, state);

/** The how-to table for a state (only calibration names the state; Utah's is HOW_TO). */
export function howToTableFor(state?: UsState): Record<string, HowTo> {
  const w = words(state);
  return {
  calibration: {
    need: ["A large glass", "Crushed ice and cold water", "Your probe thermometer"],
    noBuy: { "A large glass": "a glass from your cupboard", "Crushed ice and cold water": "ice and tap water" },
    steps: [
      "Fill the glass with crushed ice.",
      "Add cold water to the top.",
      "Stir, then wait about 30 seconds, until the number stops changing.",
      "Put the probe thermometer in the middle of the glass.",
      "Keep it off the sides and bottom.",
      "Check that it reads 32°F.",
      "Fix it the way the maker says, or use another thermometer.",
      "Test it again.",
      ...(w.boilingStep ? [w.boilingStep] : []),
    ],
    why: "Every temperature you log is only as true as the thermometer.",
    citations: [cite("Source protocols: thermometer calibration, ice-point method", w.noBoilingCite)],
  },
  cold_units: {
    need: ["The thermometer inside each fridge and freezer, or your calibrated probe"],
    steps: [
      "Read the thermometer in the middle, not by the door.",
      "Check fridges and coolers: 41°F or colder.",
      "Check freezers: 0°F or colder.",
      "Type each unit's temperature.",
      "Tell your manager if one is too warm.",
      "Write what you did, like moving the food to a working fridge.",
    ],
    why: "Germs grow fast above 41°F. Catching a warm fridge early saves the food and your guests.",
    citations: ["Food Code 3-501.16"],
  },
  restroom_check: {
    need: ["Refills: hand soap and paper towels", "Refills: toilet paper", "The restroom cleaning set (its own labeled cloths and mop)"],
    steps: [
      "Check for soap, paper towels or a dryer, and toilet paper.",
      "Check for warm water at the sink.",
      "Check that it is clean and the trash is not overflowing.",
      "Check that the door closes by itself.",
      "Fix anything missing now, and write what you did.",
      "Use only the restroom cloths and mop.",
      "Never take them into the kitchen.",
      "Wash your hands when you finish.",
      "Wash again at the kitchen sink before touching food.",
    ],
    why: "Restrooms are where hands pick up the germs that make people sick. A stocked sink makes handwashing possible.",
    citations: ["Food Code 6-301.11", "Food Code 6-301.12", "Food Code 6-202.14", "Food Code 6-501.16"],
  },
  wiping_cloths: CLOTHS,
  };
}

export const HOW_TO: Record<string, HowTo> = howToTableFor("UT");

/** How-to for an expected check, by its template (sanitizer depends on how the location sanitizes). */
export function howToFor(templateId: string, opts: { sanitizer?: SanitizerSetup; recheck?: boolean; state?: UsState } = {}): HowTo | undefined {
  if (templateId === "sanitizer") return sanitizerHowTo(opts.sanitizer, !!opts.recheck);
  return opts.state ? howToTableFor(opts.state)[templateId] : HOW_TO[templateId];
}
